import React, { useCallback, useMemo } from 'react';

import { Form, Select } from 'antd';

import type { FieldProps } from 'formik';
import { getIn } from 'formik';
import { Hint } from 'v2/components/Shared/Hint';

interface ISelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface IProps extends FieldProps {
  label?: string;
  labelItens?: string;
  multiple?: boolean;
  native?: boolean;
  options: ISelectOption[];
  loading?: boolean;
  emptyOption?: string;
  onChange?: (e: React.ChangeEvent<{ name?: string; value: unknown }>) => void;
  helperText?: React.ReactNode;
  placeholder?: string;
  status?: 'error';
  layout?: 'vertical' | 'horizontal';
  disabled?: boolean;
}

const MultipleValues = ({
  options = [],
  field,
  label,
  loading,
  placeholder = 'Selecione',
  form,
  status,
  disabled
}: IProps) => {
  const hasNoOptions = !options?.length;

  const handleChange = (values: any[]) => {
    form.setFieldValue(field.name, values);
  };

  const placeholderText = loading ? 'Carregando...' : hasNoOptions ? `Não há ${label?.toLowerCase()}` : placeholder;

  return (
    <Select
      mode='multiple'
      className='w-full'
      value={field.value || []}
      onChange={handleChange}
      placeholder={placeholderText}
      disabled={disabled || hasNoOptions}
      status={status}
      options={options?.map(op => ({
        label: op.label,
        value: op.value,
        disabled: op.disabled
      }))}
    />
  );
};

const SingleValue = ({
  options = [],
  field,
  label,
  loading,
  emptyOption,
  placeholder = 'Selecione uma opção',
  form,
  disabled,
  ...props
}: IProps) => {
  const hasNoOptions = !options?.length;

  const handleChange = useCallback(
    (value: any) => {
      const syntheticEvent = { target: { name: field.name, value } } as any;
      props?.onChange?.(syntheticEvent);
      form.setFieldValue(field.name, value);
    },
    [field, form, props]
  );

  const placeholderText = loading ? 'Carregando...' : hasNoOptions ? `Não há ${label?.toLowerCase()}` : placeholder;

  const selectOptions = useMemo(() => {
    const opts =
      options?.map(op => ({
        label: op.label,
        value: op.value,
        disabled: op.disabled
      })) || [];

    if (emptyOption) {
      opts.unshift({ label: emptyOption, value: '' as any, disabled: false });
    }

    return opts;
  }, [options, emptyOption]);

  return (
    <Select
      className='w-full'
      value={field.value == null ? undefined : field.value}
      onChange={handleChange}
      placeholder={placeholderText}
      disabled={disabled}
      options={selectOptions}
      status={props.status}
    />
  );
};

export const SelectFormField = ({ field, form, label, helperText, layout = 'vertical', ...props }: IProps) => {
  const errorText = getIn(form.errors, field.name);

  return (
    <Hint inputName={field.name}>
      <Form.Item
        className='mb-0 w-full'
        label={label}
        help={errorText || helperText}
        validateStatus={errorText ? 'error' : undefined}
        layout={layout}
      >
        {props.multiple ? (
          <MultipleValues field={field} form={form} label={label} status={errorText ? 'error' : undefined} {...props} />
        ) : (
          <SingleValue field={field} form={form} label={label} status={errorText ? 'error' : undefined} {...props} />
        )}
      </Form.Item>
    </Hint>
  );
};
